import { useEffect, useRef, useState } from "react";
import Button from "@mui/material/Button";
import { BellOff, BellRing } from "lucide-react";
import { useApp } from "../context/AppContext";
import { browserNotificationsEnabled, browserNotificationsSupported, notificationWorker, setBrowserNotificationsEnabled } from "../lib/follow-notifications";

export function BrowserNotificationSettings() {
  const { user } = useApp();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");
  const userIdRef = useRef(user?.id);
  userIdRef.current = user?.id;
  const operationRef = useRef(0);
  const supported = browserNotificationsSupported();
  const denied = supported && Notification.permission === "denied";
  useEffect(() => {
    operationRef.current += 1;
    setBusy(false); setFeedback("");
    const refresh = () => setEnabled(Boolean(user && browserNotificationsEnabled(user.id)));
    refresh();
    window.addEventListener("focus", refresh); window.addEventListener("storage", refresh);
    return () => { operationRef.current += 1; window.removeEventListener("focus", refresh); window.removeEventListener("storage", refresh); };
  }, [user?.id]);
  const toggle = async () => {
    if (!user || busy) return;
    const userId = user.id;
    const operation = ++operationRef.current;
    const active = () => userIdRef.current === userId && operationRef.current === operation;
    setBusy(true); setFeedback("");
    try {
      if (enabled) {
        setBrowserNotificationsEnabled(user.id, false); setEnabled(false);
        setFeedback("已关闭浏览器通知，站内提醒仍会保留。");
      } else {
        const permission = await Notification.requestPermission();
        if (!active()) return;
        if (permission !== "granted") {
          setFeedback(permission === "denied" ? "通知权限已关闭，可在浏览器的网站设置中开启。" : "暂未开启，站内消息仍会提醒你。");
          return;
        }
        await notificationWorker();
        if (!active()) return;
        setBrowserNotificationsEnabled(user.id, true); setEnabled(true);
        setFeedback("已开启。请保持朋友圈页面打开，切换到其他页面时可收到通知栏提醒。");
      }
    } catch { if (active()) setFeedback("通知暂时无法开启，请稍后重试。站内消息不受影响。"); }
    finally { if (active()) setBusy(false); }
  };
  if (!user) return null;
  return <aside className="browser-notification-settings" aria-label="新文案通知设置">
    <div><strong>关注动态提醒</strong><p>关注的人发布公开文案后，消息中会出现未读提醒。打开朋友圈时也会弹出提示。</p>
      <small role="status">{feedback || (!supported ? "当前浏览器暂不支持通知栏提醒，仍可在消息中查看更新。" : denied ? "通知权限已关闭，可在浏览器的网站设置中开启。" : enabled ? "浏览器通知已开启；请保持朋友圈页面打开以接收即时提醒。" : "可开启通知栏提醒，在切换到其他页面时收到新文案提示。")}</small>
    </div>
    {supported ? <Button type="button" variant="outlined" disabled={busy || denied} startIcon={enabled ? <BellOff /> : <BellRing />} onClick={() => void toggle()}>{busy ? "正在开启…" : enabled ? "关闭浏览器通知" : "开启浏览器通知"}</Button> : null}
  </aside>;
}
